import React, { useState } from 'react';
import { createMatch } from '../../services/leagueService';
import {
  generateRoundRobinSchedule,
  generateEliminationBracket,
  calculateRoundRobinMatches,
  calculateEliminationMatches
} from '../../utils/scheduleGenerator';

const GenerateScheduleModal = ({ leagueId, league, members, onClose, onSuccess }) => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [options, setOptions] = useState({
    rounds: 1,
    randomize: false,
    startDate: '',
    location: league?.location || ''
  });

  const isRoundRobin = league?.format === 'round-robin';
  const isElimination = league?.format?.includes('elimination');

  const totalMatches = isRoundRobin
    ? calculateRoundRobinMatches(members.length, options.rounds)
    : isElimination
    ? calculateEliminationMatches(members.length)
    : 0;

  const handleGenerate = async () => {
    if (members.length < 2) {
      setError('Need at least 2 members to generate a schedule');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      let matches = [];

      if (isRoundRobin) {
        matches = generateRoundRobinSchedule(members, options.rounds);
      } else if (isElimination) {
        matches = generateEliminationBracket(members, options.randomize);
      }

      // Add location if provided
      if (options.location) {
        matches = matches.map(match => ({
          ...match,
          location: options.location
        }));
      }

      // Create all matches in Firestore
      const createPromises = matches.map(match =>
        createMatch(leagueId, match)
      );

      await Promise.all(createPromises);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error generating schedule:', err);
      setError('Failed to generate schedule. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="ls-generate-schedule-modal">
      <div className="ls-modal-content">
        <div className="ls-modal-header">
          <h2>Generate Schedule</h2>
          <button onClick={onClose} className="ls-close-btn" aria-label="Close modal">
            <IconCircleX size={24} />
          </button>
        </div>

        <div className="ls-modal-body">
          <div className="ls-schedule-info">
            <IconInfoCircle size={20} color="#00d4ff" />
            <div className="ls-info-text">
              <p className="ls-info-title">
                {isRoundRobin ? 'Round Robin Schedule' : 'Elimination Bracket'}
              </p>
              <p className="ls-info-description">
                {isRoundRobin
                  ? 'Every participant will play against every other participant'
                  : 'Single-elimination tournament bracket with automatic advancement'}
              </p>
            </div>
          </div>

          <div className="ls-schedule-stats">
            <div className="ls-stat-item">
              <span className="ls-stat-label">Participants</span>
              <span className="ls-stat-value">{members.length}</span>
            </div>
            <div className="ls-stat-item">
              <span className="ls-stat-label">Total Matches</span>
              <span className="ls-stat-value">{totalMatches}</span>
            </div>
          </div>

          {isRoundRobin && (
            <div className="ls-form-group">
              <label htmlFor="rounds">Number of Rounds</label>
              <select
                id="rounds"
                value={options.rounds}
                onChange={(e) => setOptions({ ...options, rounds: Number(e.target.value) })}
              >
                <option value="1">Single Round Robin (everyone plays once)</option>
                <option value="2">Double Round Robin (everyone plays twice)</option>
                <option value="3">Triple Round Robin (everyone plays three times)</option>
              </select>
              <p className="ls-form-hint">
                Each participant will play {members.length - 1} matches per round
              </p>
            </div>
          )}

          {isElimination && (
            <div className="ls-form-group">
              <label>
                <input
                  type="checkbox"
                  checked={options.randomize}
                  onChange={(e) => setOptions({ ...options, randomize: e.target.checked })}
                />
                <span>Randomize seeding</span>
              </label>
              <p className="ls-form-hint">
                {options.randomize
                  ? 'Participants will be randomly placed in the bracket'
                  : 'Participants will be placed in order as they joined'}
              </p>
            </div>
          )}

          <div className="ls-form-group">
            <label htmlFor="location">Default Location (Optional)</label>
            <input
              type="text"
              id="location"
              value={options.location}
              onChange={(e) => setOptions({ ...options, location: e.target.value })}
              placeholder="e.g., Central Park Courts"
            />
            <p className="ls-form-hint">This location will be applied to all generated matches</p>
          </div>

          {error && (
            <div className="ls-error-message">{error}</div>
          )}
        </div>

        <div className="ls-modal-actions">
          <button
            type="button"
            onClick={onClose}
            className="ls-btn-secondary"
            disabled={generating}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            className="ls-btn-primary"
            disabled={generating}
          >
            {generating ? 'Generating...' : `Generate ${totalMatches} Matches`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateScheduleModal;
